import {gsap} from "gsap";
import {Container} from "pixi.js";
import {getRandomNum} from "../utilites";
import {ReelSymbol} from "./reelSymbol";
import {multipliersNumsById, SYMBOLS, VISUAL_HIGHER_ORDER_ICONS_NAMES} from "../constants/configs";
import {getDurations, getStores} from "./spin";
import {DURATIONS} from "../constants/durations";
import {pixiComponents} from "../index";
import {reelStopSound} from "../soundService";

export class Reel {
    container = new Container();
    symbols = [];

    constructor({width, height, symbolsCount, parentBoard}) {
        this.width = width;
        this.height = height;
        this.symbolsCount = symbolsCount;
        this.symbolHeight = this.height / this.symbolsCount;

        this.createSymbols();
    }

    createSymbols() {
        this.symbols = [];
        this.container.pivot.set(this.width / 2, 0);
        for (let i = 0; i < this.symbolsCount; i++) {
            const symbol = new ReelSymbol({parentReel: this, iconName: `icon${getRandomNum(1, 9)}`});
            symbol.animatedSprite.y = i * this.symbolHeight + this.symbolHeight / 2;
            symbol.animatedSprite.x = this.width / 2;
            this.symbols.push(symbol);
            this.container.addChild(symbol.animatedSprite);
        }
    }

    clear(clearSymbolsDelay, clearSymbolDuration) {
        return new Promise((resolve) => {
            let completed = 0;
            this.symbols.forEach((symbol, i) => {
                symbol.gsapAnimation = gsap.delayedCall((this.symbols.length - i) * clearSymbolsDelay, () => {
                    symbol.gsapAnimation = gsap.to(symbol.animatedSprite, {
                        y: this.height * 1.3,
                        duration: (this.symbols.length - i) * clearSymbolsDelay + clearSymbolDuration,
                        ease: "power1.in",
                        onComplete: () => {
                            completed += 1;
                            if (this.symbolsCount <= completed) {
                                resolve();
                            }
                        },
                    });
                })
            })
        })
    }

    killSymbolsGsapAnimation() {
        this.symbols.forEach((symbol, i) => {
            symbol.gsapAnimation?.kill();
            // symbol.animatedSprite.y = i * this.symbolHeight - this.height;
        })
    }

    throw(reelSymbols) {
        if(!getStores().spinStore.getIsTurbo() && !getStores().spinStore.isQuickFall){
            reelStopSound.play();
        }
        return new Promise((resolve) => {
            let throwAnimationCompletedSymbolsCount = 0;
            this.symbols.forEach((symbol, i) => {
                symbol.animatedSprite.y = i * this.symbolHeight - this.height;
            })
            this.symbols.forEach((symbol, i) => {
                gsap.delayedCall((this.symbols.length - i) * getDurations(DURATIONS.DURATION_NAMES.throwSymbolsDelay), () => {
                    symbol.setIcon(SYMBOLS[reelSymbols[i]]);
                    if (multipliersNumsById[reelSymbols[i]]) {
                        pixiComponents.slotBoard.multipliersBoard.requireMultiplierIcon(multipliersNumsById[reelSymbols[i]], symbol);
                    }
                    if (i === this.symbols.length - 1) {
                        this.setIconsBringToFrontVisual();
                    }
                    gsap.to(symbol.animatedSprite, {
                        duration: getDurations(DURATIONS.DURATION_NAMES.throwSymbolDuration),
                        y: i * this.symbolHeight + this.symbolHeight / 2,
                        onComplete: () => symbol.playYBrakingAnimation().then(() => {
                            throwAnimationCompletedSymbolsCount += 1;
                            if (throwAnimationCompletedSymbolsCount === this.symbols.length) {
                                resolve();
                            }
                        }),
                        ease: "back.out(0.8)",
                    })
                })
            })
        })
    }

    rearrangeChildrenBySymbolsArrangement() {
        this.container.children.forEach((_, i) => {
            this.container.children[i] = this.symbols[i].animatedSprite;
        });
        this.setIconsBringToFrontVisual();
    }

    condenseSymbols() {
        return new Promise((resolve) => {
            let animationCount = 0;
            let finishedAnimationsCount = 0;

            this.symbols.forEach((symbol, index) => {
                const newY = index * this.symbolHeight + this.symbolHeight / 2;
                if (Math.floor(symbol.animatedSprite.y) !== Math.floor(newY)) {
                    animationCount += 1;
                    gsap.delayedCall((this.symbols.length - index) * 0.04, () => {
                        gsap.to(symbol.animatedSprite, {
                            duration: getDurations(DURATIONS.DURATION_NAMES.condenseSymbolDuration),
                            y: newY,
                            onComplete: async () => {
                                await symbol.playYBrakingAnimation();
                                finishedAnimationsCount += 1;
                                if (finishedAnimationsCount === animationCount) {
                                    resolve();
                                }
                            },
                            ease: "back.out(0.8)",
                        })
                    })
                }
            })

            if (!animationCount) {
                resolve();
            }
        })
    }

    setSymbols(symbols) {
        this.symbols.forEach((symbol, i) => {
            symbol.setIcon(SYMBOLS[symbols[i]]);
        });
        this.setIconsBringToFrontVisual();
    }

    setIconBringToFrontVisual(iconName) {
        this.symbols.forEach((symbol, i) => {
            if (symbol.iconName === iconName) {
                symbol.bringToFrontVisual();
            }
        });
    }

    setIconsBringToFrontVisual() {
        VISUAL_HIGHER_ORDER_ICONS_NAMES.reverse().forEach((iconName) => this.setIconBringToFrontVisual(iconName));
    }

    explodeSymbolsByName(explodingIconsNames, newSymbols) {
        return new Promise((resolve) => {
            const explodingSymbols = [];
            let explodedIconsCount = 0;

            this.symbols.forEach((symbol) => {
                if (explodingIconsNames.includes(symbol.iconName)) {
                    explodingSymbols.push(symbol);
                }
            })

            explodingSymbols.forEach((symbol) => {
                symbol.explode().then(() => {
                    const newSymbolId = newSymbols.pop();
                    symbol.setIcon(SYMBOLS[newSymbolId]);
                    if (multipliersNumsById[newSymbolId]) {
                        pixiComponents.slotBoard.multipliersBoard.requireMultiplierIcon(multipliersNumsById[newSymbolId], symbol);
                    }
                    symbol.sendToUp();
                    explodedIconsCount += 1;
                    if (explodedIconsCount === explodingSymbols.length) {
                        resolve();
                    }
                });
            })

            if (!explodingSymbols.length) {
                resolve();
            }
        })
    }

}